import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Church } from "lucide-react";
import { toast } from "sonner";

const LITURGY_TYPES = ["Novus Ordo", "Traditional Latin Mass (TLM)", "Byzantine", "Maronite", "Ordinariate", "Other"];
const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Daily"];
const LANGUAGES = ["English", "Spanish", "Latin", "Polish", "Vietnamese", "Filipino", "Other"];

export default function MassScheduleDialog({ onSave, editData = null, trigger }) {
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState({
    day: "Sunday",
    time: "",
    liturgy_type: "Novus Ordo",
    language: "English",
    notes: ""
  });

  useEffect(() => {
    if (open && editData) {
      setSchedule(editData);
    } else if (open && !editData) {
      setSchedule({ day: "Sunday", time: "", liturgy_type: "Novus Ordo", language: "English", notes: "" });
    }
  }, [open, editData]);

  const handleSave = () => {
    if (!schedule.time) {
      toast.error("Please enter a time");
      return;
    }
    onSave(schedule);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Church className="w-5 h-5 text-[var(--liturgical-primary)]" /> {editData ? "Edit" : "Add"} Mass Schedule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">Day</Label>
              <Select value={schedule.day} onValueChange={(v) => setSchedule({...schedule, day: v})}>
                <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {DAYS_OF_WEEK.map(d => <SelectItem key={d} value={d} className="font-['Nunito'] text-sm">{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">Time (e.g., 9:00 AM)</Label>
              <Input 
                value={schedule.time} 
                onChange={(e) => setSchedule({...schedule, time: e.target.value})}
                placeholder="9:00 AM"
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">Liturgy Type</Label>
              <Select value={schedule.liturgy_type} onValueChange={(v) => setSchedule({...schedule, liturgy_type: v})}>
                <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LITURGY_TYPES.map(t => <SelectItem key={t} value={t} className="font-['Nunito'] text-sm">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">Language</Label>
              <Select value={schedule.language} onValueChange={(v) => setSchedule({...schedule, language: v})}>
                <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LANGUAGES.map(l => <SelectItem key={l} value={l} className="font-['Nunito'] text-sm">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-['Nunito']">Notes (optional)</Label>
            <Input 
              value={schedule.notes} 
              onChange={(e) => setSchedule({...schedule, notes: e.target.value})}
              placeholder="e.g., First Friday, Sung Mass"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSave} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
            {editData ? "Save Changes" : "Add Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
